import { For, If, location, reactive, watch } from 'cdui-js';
import { fetchApplyAccList, fetchProfessionalInvestorAssessmentStatus } from '../../../../api/account';
import { fetchAllInfos } from '../../../../api/open-account';
import { addAccState, nextProgress } from './store';
import { initBusConfigs, busConfigsMaps, busConfigsTick } from '../../../../stores/bus-config';
import dayts from '@china-difi/lib/date';
import { showToast } from '../../components/Toast';
import { DiFiNoData } from '../../../../components/DiFi/DiFiNoData';
import i18n from '../../../../i18n';
import { OpenAccountBootstrapTimeoutError, waitForOpenAccountBootstrap } from '../open-progress/openAccountSession';
import {
  hydrateVirtualApplyFromApplyData,
  notifyAppContinueAddVirtual,
} from './add-virtual-applyinfo/store';
import { getErrorFieldLabel, parseOpinionPayload } from '../open-progress/errorKeyNormalize';
import { AccountResult, type ResultDescriptionItem } from '../result';
import { addAccountBarPadBottom, addAccountContentReserve } from './safeAreaBar';
import { filterAddAccountDisplayLabelText, filterAddAccountDisplayTypeCodes } from './addAccountDisplayTypes';
import '../result/style.css';
import './style.css';

/** 仍在审核中的状态（不允许再次提交申请） */
const PENDING_AUDIT_STATUSES = new Set(['SUBMITTED', 'APPROVING']);

const CRYPTO_ACCOUNT = 'CRYPTO_ACCOUNT';

const getApplyStatus = (item: any) => item?.auditInfo?.status || item?.status || '';

const getAuditOpinionRaw = (item: any) => {
  const audit = item?.auditInfo || {};
  return audit.opinion ?? audit.remark ?? item?.refuseReason ?? item?.remark ?? '';
};

/** 拒绝原因：优先纯文案；opinion 数组则拼接 message */
const getRefuseReason = (item: any): string => {
  const raw = getAuditOpinionRaw(item);
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed && !trimmed.startsWith('[') && !trimmed.startsWith('{')) return trimmed;
  }
  const items = parseOpinionPayload(raw);
  const joined = items
    .map((i) => i.message)
    .filter(Boolean)
    .join('；');
  if (joined) return joined;
  return typeof raw === 'string' ? raw : '';
};

/** 退回原因：对齐开户 BACK 结果页，字段名 + 意见列表 */
const getBackReasonItems = (item: any): ResultDescriptionItem[] => {
  const raw = getAuditOpinionRaw(item);
  const items = parseOpinionPayload(raw);
  if (items.length > 0) {
    return items.map((row, index) => ({
      label: getErrorFieldLabel(row.key) || String(index + 1),
      value: row.message || '',
    }));
  }
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) return [];
  return [{ label: '1', value: text }];
};

const resolveIsMainland = (status: { isMainlandIdentity?: boolean | string | number | null }, identityType: string) => {
  const flag = status?.isMainlandIdentity;
  if (flag === true || flag === 1 || String(flag).toLowerCase() === 'true') return true;
  if (flag === false || flag === 0 || String(flag).toLowerCase() === 'false') return false;
  return identityType === 'MAINLAND_CHINA';
};

/* ── 申请状态配置（对齐后端枚举）──────────────────────────
 * SUBMITTED   已提交，等待初审
 * APPROVING   审批中（复审）
 * FINISHED    已通过 / 完成
 * REFUSED     已拒绝
 * BACK        已退回（需补充资料）
 * CANCELLED   已取消
 * ─────────────────────────────────────────────────────── */
const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  SUBMITTED: { bg: 'rgba(255, 167, 72, 0.2)', color: '#FFA43D' },
  APPROVING: { bg: 'rgba(43, 168, 247, 0.2)', color: '#2BA8F7' },
  FINISHED: { bg: 'rgba(66, 255, 132, 0.2)', color: '#2BB981' },
  REFUSED: { bg: 'rgba(255, 76, 97, 0.2)', color: '#FF4C61' },
  BACK: { bg: 'rgba(255, 167, 72, 0.2)', color: '#FFA43D' },
  CANCELLED: { bg: 'rgba(162, 167, 173, 0.2)', color: '#A2A7AD' },
  STOP: { bg: 'rgba(162, 167, 173, 0.2)', color: '#A2A7AD' },
};

const getStatusLabel = (code: string) => {
  const A = i18n.AddAccount;
  const map: Record<string, string> = {
    SUBMITTED: A.StatusSubmitted,
    APPROVING: A.StatusApproving,
    FINISHED: A.StatusPassed,
    REFUSED: A.StatusRefused,
    BACK: A.StatusBack,
    CANCELLED: A.StatusCancelled,
    STOP: A.StatusStopped,
  };
  return map[code] ?? code;
};

const getStatus = (code: string) => {
  const style = STATUS_STYLE[code] ?? { bg: 'var(--bg-elevated)', color: 'var(--text-3)' };
  return { ...style, label: getStatusLabel(code) };
};

/** 将 securitiesAccountType + fundAccountType 合并为可读标签（见 addAccountDisplayTypes：衍生/A股通已迁交易品种） */
const getAccTypeLabel = (item: any): string => {
  const secMap = busConfigsMaps.securitiesAccountType as Record<string, string>;
  const fundMap = busConfigsMaps.fundAccountType as Record<string, string>;
  const rawCodes = [...(item.securitiesAccountType || '').split(','), ...(item.fundAccountType || '').split(',')].filter(
    Boolean,
  );
  const codes = filterAddAccountDisplayTypeCodes(rawCodes, secMap, fundMap);
  const labels = codes.map((c) => secMap[c] ?? fundMap[c] ?? c);
  if (labels.length > 0) return labels.join('、');
  const fromAccountType = filterAddAccountDisplayLabelText(item.accountType);
  return fromAccountType || '--';
};

const ApplyList = () => {
  const state = reactive({
    list: [] as any[],
    loading: true,
    resuming: false,
    statusFilter: '',
    timeFilter: '',
    /** 已拒绝详情层：非 null 时覆盖列表 */
    refuseItem: null as any | null,
    /** 已退回原因层：非 null 时覆盖列表 */
    backItem: null as any | null,
    dictRev: 0,
  });

  watch(
    () => busConfigsTick.v,
    (v) => {
      state.dictRev = v;
    },
  );

  const accTypeLabel = (item: any) => {
    void state.dictRev;
    return getAccTypeLabel(item);
  };

  const loadList = async () => {
    state.loading = true;
    try {
      // App WebView：等本次 OpenAccount Bootstrap 后再拉鉴权接口
      await waitForOpenAccountBootstrap();
      // 一进入即请求申请列表；字典与列表并行拉取
      const [, res] = await Promise.all([initBusConfigs(), fetchApplyAccList()]);
      state.list = (res as any)?.data ?? res ?? [];
    } catch (e) {
      if (e instanceof OpenAccountBootstrapTimeoutError) {
        showToast(i18n.OpenProgress.Errors.AuthExpired);
      }
    } finally {
      state.loading = false;
    }
  };

  loadList();

  const handleApply = () => {
    // 列表尚未返回时不允许跳转，避免漏检审核中申请
    if (state.loading || state.resuming) return;
    const hasPending = state.list.some((item) => PENDING_AUDIT_STATUSES.has(getApplyStatus(item)));
    if (hasPending) {
      showToast(i18n.AddAccount.ToastApproving);
      return;
    }
    // 有已退回单：立即申请直接进续办（与原点击该条记录相同）
    const backItem = state.list.find((item) => getApplyStatus(item) === 'BACK');
    if (backItem) {
      void handleResumeBack(backItem);
      return;
    }
    // 正常新申请：确保不带上一次 BACK 续办标记
    addAccState.riskResumeFromList = false;
    nextProgress();
  };

  const handleCardClick = (item: any, statusCode: string) => {
    if (statusCode === 'BACK') {
      showToast(i18n.AddAccount.ToastHasBack);
      return;
    }
  };

  const handleShowRefuseReason = (item: any, e?: Event) => {
    e?.stopPropagation?.();
    state.refuseItem = item;
  };

  const handleShowBackReason = (item: any, e?: Event) => {
    e?.stopPropagation?.();
    state.backItem = item;
  };

  /** BACK 续办：同单回填，不开新单 */
  const handleResumeBack = async (item: any) => {
    if (state.resuming || state.loading) return;
    if (getApplyStatus(item) !== 'BACK') return;

    state.resuming = true;
    try {
      const busId = String(item?.id || '');
      const secMap = busConfigsMaps.securitiesAccountType as Record<string, string>;
      const fundMap = busConfigsMaps.fundAccountType as Record<string, string>;
      const codes = filterAddAccountDisplayTypeCodes(
        String(item?.securitiesAccountType || '')
          .split(',')
          .filter(Boolean),
        secMap,
        fundMap,
      );
      const secType = codes.includes(CRYPTO_ACCOUNT) ? CRYPTO_ACCOUNT : codes[0] || '';
      const fundType = String(item?.fundAccountType || '')
        .split(',')
        .filter(Boolean)[0] || '';

      addAccState.busId = busId;
      addAccState.securitiesAccountType = codes.join(',') || secType;
      addAccState.fundAccountType = fundType;
      /** 衍生品权限已迁 App「交易品种」，加开 BACK 续办不再带 needDerivativeTradePermission */
      addAccState.needDerivativeTradePermission = false;

      // 虚拟资产：拉详情回填后进境外身份补充 / 交 App
      if (secType === CRYPTO_ACCOUNT) {
        const [applyData, piStatus] = await Promise.all([
          fetchAllInfos('ADD_ACCOUNT') as Promise<any>,
          fetchProfessionalInvestorAssessmentStatus().catch(() => null),
        ]);

        const detail = applyData?.id ? applyData : { ...item, userBase: applyData?.userBase };
        const userBase = detail?.userBase || applyData?.userBase || {};
        // 大陆身份以 identityType 为准（BACK 后 idCardType 可能已是境外证件）
        const identityType = userBase.identityType || '';
        const isMainland = resolveIsMainland(piStatus || {}, identityType);

        if (isMainland) {
          await hydrateVirtualApplyFromApplyData({
            ...detail,
            id: detail?.id || busId,
            attachmentList: detail?.attachmentList || applyData?.attachmentList || [],
            userBase,
          });
          addAccState.fullName = userBase.fullName || addAccState.fullName;
          addAccState.virtualFlow = true;
          return;
        }

        notifyAppContinueAddVirtual({
          busId: busId || detail?.id,
          isMainlandIdentity: false,
        });
        if (!(window as any).ReactNativeWebView) {
          showToast(i18n.AddAccount.ToastVirtualInApp);
        }
        return;
      }

      // 非虚拟：带上已选类型，从风险披露续办（上一步回列表，不经选类型）
      addAccState.riskResumeFromList = true;
      addAccState.step = 3;
    } catch (e) {
      const msg = typeof e === 'string' ? e : (e as { message?: string })?.message;
      showToast(msg || i18n.AddAccount.ToastOpFailed);
    } finally {
      state.resuming = false;
    }
  };

  return (
    <div>
      {/* ── 已拒绝结果层 ── */}
      <If when={!!state.refuseItem}>
        <div
          class="fixed position-t position-b position-l position-r bg-c"
          style={{ 'z-index': 200 }}
        >
          <AccountResult
            status="fail"
            title={i18n.AddAccount.RefuseTitle}
            buttonText={i18n.OpenProgress.Common.Back}
            onButtonClick={() => {
              state.refuseItem = null;
            }}
            contentRender={() => {
              const reason = getRefuseReason(state.refuseItem);
              return (
                <div class="flex flex-column align-center text-center">
                  <If when={!!reason}>
                    <div class="font-s-S color" style={{ 'line-height': '20px' }}>
                      {reason}
                    </div>
                  </If>
                  <div class="font-s-S color" style={{ 'line-height': '20px' }}>
                    {i18n.AddAccount.RefuseContactPrefix}{' '}
                    <span
                      class="link-color"
                      onclick={() => location.routeTo('/ai-chat?scene=customer')}
                    >
                      {i18n.AccountUnopened.ContactService}
                    </span>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </If>

      {/* ── 已退回原因层（对齐开户 BACK 结果页） ── */}
      <If when={!!state.backItem}>
        <div
          class="fixed position-t position-b position-l position-r bg-c"
          style={{ 'z-index': 200 }}
        >
          <AccountResult
            status="warning"
            title={i18n.OpenProgress.Entry.ResultBackTitle}
            description={i18n.OpenProgress.Entry.ResultBackDesc}
            descriptionItems={getBackReasonItems(state.backItem)}
            buttonText={i18n.OpenProgress.Common.Back}
            onButtonClick={() => {
              state.backItem = null;
            }}
          />
        </div>
      </If>

      {/* ── 申请列表 ── */}
      {/* 底栏 ≈ 16+50+16；再加安全区，避免列表末尾被挡 */}
      <div
        class="padding flex flex-column gap-12px"
        style={{ 'margin-bottom': `${addAccountContentReserve(82)}px` }}
      >
        <If when={!state.loading && state.list.length === 0}>
          <DiFiNoData hasIcon style={{ padding: '60px 0' }} />
        </If>
        <For each={state.list}>
          {(item) => {
            const statusCode = getApplyStatus(item);
            const status = getStatus(statusCode);
            const accType = accTypeLabel(item);
            const submitTime = dayts.format(+item.createTime) || dayts.format(+item.submitTime) || '--';
            const clickable = statusCode === 'BACK';
            return (
              <div
                class="add-account-apply-card round flex flex-column gap-10px"
                style={{
                  padding: '16px',
                  cursor: clickable ? 'pointer' : 'default',
                  opacity: state.resuming ? 0.7 : 1,
                }}
                onclick={() => handleCardClick(item, statusCode)}
              >
                <div
                  class="flex align-center justify-space-between font-s-S"
                  style={{ 'line-height': '22px', 'min-height': '22px' }}
                >
                  <span style={{ color: 'var(--text-3)' }}>{i18n.AddAccount.ColAccountType}</span>
                  <span class="color-D">{accType}</span>
                </div>
                <div
                  class="flex align-center justify-space-between font-s-S"
                  style={{ 'line-height': '22px', 'min-height': '22px' }}
                >
                  <span style={{ color: 'var(--text-3)' }}>{i18n.AddAccount.ColSubmitTime}</span>
                  <span class="color-D">{submitTime}</span>
                </div>
                <div
                  class="flex align-start justify-space-between font-s-S"
                  style={{ 'line-height': '22px', 'min-height': '22px' }}
                >
                  <span style={{ color: 'var(--text-3)' }}>{i18n.AddAccount.ColApplyStatus}</span>
                  <div class="flex flex-column align-end gap-4px">
                    <div
                      class="round-XS font-s-S"
                      style={{
                        background: status.bg,
                        color: status.color,
                        padding: '4px 8px',
                        'line-height': '22px',
                      }}
                    >
                      {status.label || i18n.AddAccount.StatusPending}
                    </div>
                    <If when={statusCode === 'REFUSED'}>
                      <span
                        class="link-color font-s-S"
                        style={{ 'line-height': '22px' }}
                        onclick={(e: Event) => handleShowRefuseReason(item, e)}
                      >
                        {i18n.AddAccount.BtnRefuseReason}
                      </span>
                    </If>
                  </div>
                </div>
                <If when={statusCode === 'BACK'}>
                  <button
                    class="default-btn btn-rounded width-100"
                    style={{ height: '40px' }}
                    onclick={(e: Event) => handleShowBackReason(item, e)}
                  >
                    {i18n.AddAccount.BtnBackReason}
                  </button>
                </If>
              </div>
            );
          }}
        </For>
      </div>

      {/* ── 底部按钮 ── */}
      <div
        class="fixed position-b position-l width-100 bg-c"
        style={{
          'padding-top': '16px',
          'padding-left': '16px',
          'padding-right': '16px',
          'padding-bottom': `${addAccountBarPadBottom(16)}px`,
          'margin-top': '24px',
          'z-index': 150,
        }}
      >
        <button
          class="confirm-btn btn-rounded"
          style={{
            width: '100%',
          }}
          disabled={state.loading || state.resuming}
          onclick={handleApply}
        >
          {i18n.AddAccount.ApplyNow}
        </button>
      </div>
    </div>
  );
};

export default ApplyList;
